import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Upload, ScanLine, ClipboardList } from 'lucide-react';

const steps = [
  {
    title: 'Prepare & upload',
    description:
      'A guided intake checks naming, format, and year references so the review begins with clean context.',
    icon: Upload,
  },
  {
    title: 'Analyse with intent',
    description:
      'The statement is scored against each criterion in the framework you choose, and a summary report is written from those scores.',
    icon: ScanLine,
  },
  {
    title: 'Deliver clarity',
    description:
      'Receive a structured briefing with strengths, remediation priorities, and recommended timelines ready for execution.',
    icon: ClipboardList,
  },
];

export function HowItWorks() {
  const reduceMotion = useReducedMotion();
  return (
    <section id="how-it-works" className="w-full bg-[var(--surface-ambient)]/70 py-24">
      <div className="mx-auto flex max-w-6xl flex-col gap-16 px-6">
        <div className="max-w-2xl space-y-4">
          <p className="eyebrow">
            Flow of work
          </p>
          <h2 className="font-display text-3xl font-semibold !tracking-display text-[var(--text-primary)] md:text-4xl">
            Three deliberate stages preserve focus while keeping momentum.
          </h2>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {steps.map((step, index) => (
            <motion.div
              key={step.title}
              className="flex flex-col gap-6 rounded-3xl border border-[rgba(31,76,84,0.12)] bg-white/80 p-8 shadow-[0_22px_44px_rgba(15,23,42,0.08)]"
              initial={reduceMotion ? false : { opacity: 0, y: 24 }}
              whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: index * 0.1, ease: 'easeOut' }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[rgba(31,76,84,0.08)] text-[var(--accent)]">
                <step.icon className="h-5 w-5" />
              </span>
              <div className="space-y-3">
                <p className="eyebrow-muted">
                  Step {index + 1}
                </p>
                <h3 className="text-xl font-semibold text-[var(--text-primary)]">{step.title}</h3>
                <p className="text-sm leading-relaxed text-[var(--text-secondary)]">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
